import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight, ArrowLeft } from 'lucide-react';
import { useWorkbenchContext } from '../../hooks/useWorkbenchContext';
import apiClient from '../../api/client';

interface WorkbenchContextBarProps {
  bailOutText?: string;
}

const WorkbenchContextBar: React.FC<WorkbenchContextBarProps> = ({ bailOutText = 'Back to file \u2014 nothing saved' }) => {
  const ctx = useWorkbenchContext();
  const navigate = useNavigate();

  const hasContext = !!(ctx.projectId || ctx.fileId || ctx.moleculeId);

  // Resolve project name if we have an ID but no name
  const [resolvedProjectName, setResolvedProjectName] = useState<string | null>(null);
  useEffect(() => {
    if (ctx.projectName || !ctx.projectId) return;
    apiClient.get(`/projects/${ctx.projectId}`)
      .then(r => setResolvedProjectName(r.data?.name || `Project ${ctx.projectId}`))
      .catch(() => setResolvedProjectName(`Project ${ctx.projectId}`));
  }, [ctx.projectId, ctx.projectName]);

  const projectDisplay = ctx.projectName || resolvedProjectName || (ctx.projectId ? `Project ${ctx.projectId}` : null);
  const fileDisplay = ctx.fileName || (ctx.fileId ? `File ${ctx.fileId}` : null);
  const moleculeDisplay = ctx.moleculeName || (ctx.smiles ? (ctx.smiles.length > 24 ? ctx.smiles.slice(0, 24) + '\u2026' : ctx.smiles) : null);

  const handleBackToFile = () => {
    if (ctx.fileId) {
      navigate(`/chemistry/file/${ctx.fileId}/editor`);
    } else if (ctx.projectId) {
      navigate(`/chemistry/files?project=${ctx.projectId}`);
    }
  };

  return (
    <div style={barStyle}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 0, minWidth: 0, flex: 1 }}>
        {hasContext ? (
          <>
            {projectDisplay && (
              <>
                <button
                  onClick={() => navigate(`/chemistry/files?project=${ctx.projectId}`)}
                  style={crumbBtnStyle}
                >
                  {projectDisplay}
                </button>
                <ChevronRight size={12} style={{ color: 'var(--text-muted)', flexShrink: 0, margin: '0 2px' }} />
              </>
            )}
            {fileDisplay && (
              <>
                <button
                  onClick={() => ctx.fileId ? navigate(`/chemistry/file/${ctx.fileId}/editor`) : undefined}
                  style={crumbBtnStyle}
                >
                  {fileDisplay}
                </button>
                <ChevronRight size={12} style={{ color: 'var(--text-muted)', flexShrink: 0, margin: '0 2px' }} />
              </>
            )}
            {moleculeDisplay && (
              <span style={{ fontWeight: 600, color: 'var(--text-primary)', fontSize: 12, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {moleculeDisplay}
              </span>
            )}
            {!moleculeDisplay && !fileDisplay && projectDisplay && (
              <span style={{ color: 'var(--text-muted)', fontStyle: 'italic', fontSize: 12 }}>
                Project context
              </span>
            )}
          </>
        ) : (
          <span style={{ color: 'var(--text-muted)', fontStyle: 'italic', fontSize: 12 }}>
            Standalone mode &mdash; select a destination when saving
          </span>
        )}
      </div>

      {hasContext && (ctx.fileId || ctx.projectId) && (
        <button onClick={handleBackToFile} style={backBtnStyle}>
          <ArrowLeft size={12} />
          {bailOutText}
        </button>
      )}
    </div>
  );
};

const barStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '0 16px',
  height: 36,
  background: 'var(--hover-bg)',
  borderBottom: '0.5px solid var(--card-border)',
  fontSize: 12,
  gap: 12,
  flexShrink: 0,
};

const crumbBtnStyle: React.CSSProperties = {
  background: 'none',
  border: 'none',
  color: 'var(--text-secondary)',
  fontSize: 12,
  cursor: 'pointer',
  padding: '2px 4px',
  borderRadius: 3,
  whiteSpace: 'nowrap',
  fontFamily: 'inherit',
};

const backBtnStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 5,
  padding: '4px 10px',
  background: '#E1F5EE',
  color: '#085041',
  border: '0.5px solid #5DCAA5',
  borderRadius: 12,
  fontSize: 11,
  fontWeight: 500,
  cursor: 'pointer',
  whiteSpace: 'nowrap',
  flexShrink: 0,
  fontFamily: 'inherit',
};

export default WorkbenchContextBar;
